import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Star } from 'lucide-react';
import {
    getPublicUserProfile,
    getPublicUserListings,
    getSellerReviews,
    getSellerBreakdown,
    followUser,
    unfollowUser,
} from '../services/api.shop';
import { useMutation } from '@tanstack/react-query';
import LoadingSpinner from '../components/LoadingSpinner';
import ProductCard from '../components/ProductCard';
import ReviewSummaryCard from '../components/profile/ReviewSummaryCard';
import ReviewListItem from '../components/profile/ReviewListItem';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { useFrontendAuth } from '../hooks/useAuth';
import { invalidateSellerListingCaches } from '../utils/invalidateSellerListingCaches';
import { publicProfileHeading } from '../utils/usernameRules';

type Tab = 'listings' | 'reviews';

/**
 * Public seller profile at /users/:userId.
 * Always shows the public surface (listings, reviews, follower count).
 * Account management (following list, settings) lives on /my-account only.
 * Viewing your own URL is a public preview — same as others see (no Follow, no private tabs).
 */
const UserProfile: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const { user, isAuthenticated } = useFrontendAuth();
    const queryClient = useQueryClient();
    const [tab, setTab] = useState<Tab>('listings');

    const isOwn = !!user?.id && user.id === userId;

    // Refresh public listings/count when opening a profile (esp. after sell/delete)
    useEffect(() => {
        if (!userId) return;
        void invalidateSellerListingCaches(queryClient, userId);
    }, [queryClient, userId]);

    const { data: profile, isLoading, error } = useQuery({
        queryKey: ['publicProfile', userId],
        queryFn: () => getPublicUserProfile(userId!),
        enabled: !!userId,
    });

    const { data: listings = [] } = useQuery({
        queryKey: ['publicListings', userId],
        queryFn: () => getPublicUserListings(userId!),
        enabled: !!userId && tab === 'listings',
    });

    const { data: breakdown } = useQuery({
        queryKey: ['sellerBreakdown', userId],
        queryFn: () => getSellerBreakdown(userId!),
        enabled: !!userId,
    });

    const { data: reviews = [] } = useQuery({
        queryKey: ['sellerReviews', userId],
        queryFn: () => getSellerReviews(userId!, 1, 50),
        enabled: !!userId && tab === 'reviews',
    });

    if (isLoading) return <LoadingSpinner />;
    if (error || !profile) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-12 text-center text-red-600">
                {tr('profile.notfound', 'Profile not found.')}
            </div>
        );
    }

    const heading = publicProfileHeading({
        userName: profile.userName,
        displayName: profile.displayName,
        showPersonLine: profile.showPersonLine,
    });
    const initials = (heading.title || '?').replace(/^@/, '').charAt(0).toUpperCase();

    const memberDate = new Date(profile.memberSince).toLocaleDateString(undefined, {
        month: 'short',
        year: 'numeric',
    });

    return (
        <div className="min-h-screen">
            {isOwn && (
                <div className="border-b border-[var(--accent-color)]/30 bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)] px-4 py-2.5">
                    <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-2 text-sm">
                        <span className="text-[var(--text-color)]">
                            {tr(
                                'profile.public.preview.banner',
                                'Public profile preview - this is what other shoppers see.'
                            )}
                        </span>
                        <Link
                            to="/my-account"
                            className="font-semibold text-[var(--accent-color)] underline-offset-2 hover:underline min-h-11 inline-flex items-center"
                        >
                            {tr('profile.public.preview.back', 'Back to My account')}
                        </Link>
                    </div>
                </div>
            )}

            {/* Banner */}
            <div className="h-28 w-full bg-gradient-to-r from-[var(--accent-color)]/80 to-[color-mix(in_srgb,var(--accent-color),#6b7280_40%)]" />

            <div className="mx-auto max-w-3xl">
                {/* Header card overlap */}
                <div className="-mt-12 mb-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex items-start gap-4">
                            {profile.profilePictureUrl ? (
                                <img
                                    src={profile.profilePictureUrl}
                                    alt=""
                                    className="h-24 w-24 rounded-full object-cover ring-4 ring-[var(--bg-color)] shadow-md"
                                />
                            ) : (
                                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[var(--accent-color)] text-3xl font-bold text-white ring-4 ring-[var(--bg-color)] shadow-md">
                                    {initials}
                                </div>
                            )}
                            <div className="pt-10 sm:pt-12">
                                <div>
                                    <h1 className="text-xl font-bold text-[var(--text-color)]">
                                        {heading.title}
                                    </h1>
                                    {heading.subtitle && (
                                        <p className="mt-0.5 text-sm text-[var(--text-color)]">
                                            {heading.subtitle}
                                        </p>
                                    )}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                    {(profile.city || profile.region) && (
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin className="h-3 w-3" />
                                            {[profile.city, profile.region].filter(Boolean).join(', ')}
                                        </span>
                                    )}
                                    <span>
                                        {tr('profile.member.since', 'Member since {date}').replace(
                                            '{date}',
                                            memberDate
                                        )}
                                    </span>
                                    {profile.ratingCount > 0 && (
                                        <span className="inline-flex items-center gap-1">
                                            <Star className="h-3 w-3 fill-[var(--accent-color)] text-[var(--accent-color)]" />
                                            {profile.averageRating.toFixed(1)} ({profile.ratingCount}{' '}
                                            {tr('profile.stats.reviews', 'reviews')})
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Follow only for other users — same as a guest/shopper would see */}
                        <div className="flex flex-wrap gap-2 self-end">
                            {!isOwn && isAuthenticated && userId && (
                                <FollowButton
                                    userId={userId}
                                    initiallyFollowing={!!profile.isFollowing}
                                    onChanged={() =>
                                        queryClient.invalidateQueries({ queryKey: ['publicProfile', userId] })
                                    }
                                />
                            )}
                            {!isOwn && !isAuthenticated && (
                                <Link
                                    to="/login"
                                    className="rounded-full border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-[var(--accent-color)] px-4 py-2 text-sm font-medium text-[var(--text-color)] shadow-sm min-h-11 inline-flex items-center"
                                >
                                    {tr('follow.button', 'Follow')}
                                </Link>
                            )}
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4 border-t border-[color-mix(in_srgb,var(--accent-color),transparent_70%)] pt-5 text-center">
                        <div>
                            <p className="text-2xl font-bold text-[var(--text-color)]">
                                {profile.itemsSoldCount}
                            </p>
                            <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {tr('profile.stats.sold', 'Items sold')}
                            </p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-[var(--text-color)]">
                                {profile.activeListingsCount}
                            </p>
                            <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {tr('profile.stats.listings', 'Active listings')}
                            </p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-[var(--text-color)]">
                                {profile.ratingCount}
                            </p>
                            <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {tr('profile.stats.reviews', 'Reviews')}
                            </p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-[var(--text-color)]">
                                {profile.followerCount ?? 0}
                            </p>
                            <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {tr('profile.stats.followers', 'Followers')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-6 flex border-b border-[color-mix(in_srgb,var(--accent-color),transparent_60%)] text-sm">
                    <button
                        type="button"
                        onClick={() => setTab('listings')}
                        className={`px-4 py-2.5 font-medium transition ${
                            tab === 'listings'
                                ? 'border-b-2 border-[var(--accent-color)] text-[var(--accent-color)]'
                                : 'text-[color-mix(in_srgb,var(--text-color),transparent_40%)] hover:text-[var(--text-color)]'
                        }`}
                    >
                        {tr('profile.tab.listings', 'Listings')} ({profile.activeListingsCount})
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab('reviews')}
                        className={`px-4 py-2.5 font-medium transition ${
                            tab === 'reviews'
                                ? 'border-b-2 border-[var(--accent-color)] text-[var(--accent-color)]'
                                : 'text-[color-mix(in_srgb,var(--text-color),transparent_40%)] hover:text-[var(--text-color)]'
                        }`}
                    >
                        {tr('profile.tab.reviews', 'Reviews')} ({profile.ratingCount})
                    </button>
                </div>

                {tab === 'listings' && (
                    <div className="pb-12">
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <h2 className="text-base font-semibold text-[var(--text-color)]">
                                {tr('profile.stats.listings', 'Active listings')}
                            </h2>
                        </div>

                        {listings.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] px-6 py-10 text-center">
                                <p className="text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                    {tr('profile.listings.empty', 'No active listings.')}
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(min(100%,240px),1fr))]">
                                {listings.map((p) => (
                                    <ProductCard
                                        key={p.id}
                                        product={p}
                                        showPrice
                                        hideOwnBadge
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === 'reviews' && (
                    <div className="pb-12 space-y-4">
                        {breakdown && <ReviewSummaryCard breakdown={breakdown} />}
                        {reviews.length === 0 ? (
                            <p className="text-center text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {tr('profile.reviews.empty', 'No reviews yet.')}
                            </p>
                        ) : (
                            reviews.map((r) => <ReviewListItem key={r.id} review={r} />)
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const FollowButton: React.FC<{
    userId: string;
    initiallyFollowing: boolean;
    onChanged?: () => void;
}> = ({ userId, initiallyFollowing, onChanged }) => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const queryClient = useQueryClient();
    const [following, setFollowing] = useState(initiallyFollowing);
    const [error, setError] = useState<string | null>(null);

    React.useEffect(() => {
        setFollowing(initiallyFollowing);
    }, [initiallyFollowing]);

    const mutation = useMutation({
        mutationFn: async () => {
            if (following) await unfollowUser(userId);
            else await followUser(userId);
        },
        onSuccess: () => {
            setFollowing((v) => !v);
            setError(null);
            // My account following list uses useProtectedQuery(refetchOnMount:false) — must invalidate
            void queryClient.invalidateQueries({ queryKey: ['myFollowing'] });
            void queryClient.invalidateQueries({ queryKey: ['notifications'] });
            void queryClient.invalidateQueries({ queryKey: ['publicProfile', userId] });
            onChanged?.();
        },
        onError: (e: unknown) => {
            const msg =
                e && typeof e === 'object' && 'response' in e
                    ? (e as { response?: { data?: { errors?: { General?: string } } } }).response?.data
                          ?.errors?.General
                    : null;
            setError(msg || 'Failed');
        },
    });

    return (
        <div className="flex flex-col items-end gap-1">
            <button
                type="button"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate()}
                className={`rounded-full border px-4 py-2 text-sm font-medium shadow-sm min-h-11 transition ${
                    following
                        ? 'border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-[var(--bg-color)] text-[var(--text-color)]'
                        : 'border-transparent bg-[var(--accent-color)] text-[var(--text-color)]'
                }`}
            >
                {following
                    ? tr('follow.button.following', 'Following')
                    : tr('follow.button', 'Follow')}
            </button>
            {error && <span className="text-xs text-red-600 max-w-[12rem] text-right">{error}</span>}
        </div>
    );
};

export default UserProfile;
